import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * useComposerMentions
 * Unified mentions state and behaviors for chat composer surfaces.
 * Supports @user and #channel suggestions with dropdown positioning.
 */
export default function useComposerMentions({ editorRef, composerRef, userOptions = [], channelOptions = [] }) {
  const mentionRangeRef = useRef(null);
  const [mentionState, setMentionState] = useState({ isOpen: false, type: null, query: "", anchor: null, items: [] });
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);

  const normalizedChannelMentions = useMemo(() => (
    (channelOptions || []).map((channel) => {
      const slug = (channel?.slug || channel?.name || "").trim();
      const fallback = channel?.id != null ? String(channel.id) : "";
      const value = slug || fallback;
      const label = channel?.name?.trim() || value;
      const description = channel?.privateChannel ? 'Private channel' : (channel?.description?.trim() || "");
      const search = [value, label, description].filter(Boolean).map((s) => s.toLowerCase()).join(" ");
      return { id: String(channel?.id ?? (value || Math.random())), value, label, description, search, raw: channel };
    })
  ), [channelOptions]);

  const normalizedUserMentions = useMemo(() => (
    (userOptions || []).map((user) => {
      const username = (user?.username || "").trim();
      const description = (user?.displayName || user?.fullName || "").trim();
      const search = [username, description].filter(Boolean).map((s) => s.toLowerCase()).join(" ");
      return { id: String(user?.userId ?? user?.id ?? (username || Math.random())), value: username, label: username || "Unknown user", description, search, raw: user };
    })
  ), [userOptions]);

  const closeMentionDropdown = useCallback(() => {
    mentionRangeRef.current = null;
    setMentionState((prev) => (prev.isOpen ? { isOpen: false, type: null, query: "", anchor: null, items: [] } : prev));
    setMentionActiveIndex(0);
  }, []);

  const refreshMentionState = useCallback(() => {
    if (typeof window === "undefined") return;
    const editor = editorRef.current;
    const root = editor?.getElement?.();
    if (!root) { closeMentionDropdown(); return; }

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) { closeMentionDropdown(); return; }
    const caretRange = selection.getRangeAt(0);
    if (!root.contains(caretRange.startContainer)) { closeMentionDropdown(); return; }

    let node = caretRange.startContainer;
    let offset = caretRange.startOffset;
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (offset === 0) { closeMentionDropdown(); return; }
      let sibling = node.childNodes[offset - 1];
      while (sibling && sibling.nodeType === Node.ELEMENT_NODE && sibling.lastChild) sibling = sibling.lastChild;
      if (!sibling || sibling.nodeType !== Node.TEXT_NODE) { closeMentionDropdown(); return; }
      node = sibling; offset = sibling.textContent.length;
    }
    if (node.nodeType !== Node.TEXT_NODE) { closeMentionDropdown(); return; }

    const textBeforeCaret = node.textContent.slice(0, offset);
    const lastAt = textBeforeCaret.lastIndexOf("@");
    const lastHash = textBeforeCaret.lastIndexOf("#");
    const mentionIndex = Math.max(lastAt, lastHash);
    if (mentionIndex === -1) { closeMentionDropdown(); return; }

    const symbol = textBeforeCaret[mentionIndex];
    const queryPart = textBeforeCaret.slice(mentionIndex + 1);
    if (/\s/.test(queryPart)) { closeMentionDropdown(); return; }
    if (!/^[A-Za-z0-9_.-]*$/.test(queryPart)) { closeMentionDropdown(); return; }

    const prefixChar = mentionIndex > 0 ? textBeforeCaret[mentionIndex - 1] : "";
    if (prefixChar && !/[\s([{>]/.test(prefixChar)) { closeMentionDropdown(); return; }

    const mentionRange = document.createRange();
    mentionRange.setStart(node, mentionIndex);
    mentionRange.setEnd(node, offset);
    mentionRangeRef.current = mentionRange;

    const isUserMention = symbol === "@";
    const source = isUserMention ? normalizedUserMentions : normalizedChannelMentions;
    const normalizedQuery = queryPart.toLowerCase();
    const filtered = normalizedQuery ? source.filter((item) => item.search.includes(normalizedQuery)) : source;
    const suggestions = filtered.slice(0, 12);

    const container = composerRef.current;
    let anchor = null;
    if (container) {
      const rect = caretRange.getBoundingClientRect();
      if (rect && typeof rect.top === "number" && typeof rect.left === "number") {
        const containerRect = container.getBoundingClientRect();
        anchor = { top: rect.bottom - containerRect.top + container.scrollTop, left: rect.left - containerRect.left };
      }
    }

    setMentionActiveIndex((prev) => {
      const nextType = isUserMention ? "user" : "channel";
      if (!mentionState.isOpen || mentionState.type !== nextType || mentionState.query !== queryPart) return 0;
      if (!suggestions.length) return 0;
      return Math.min(prev, suggestions.length - 1);
    });

    setMentionState({ isOpen: true, type: isUserMention ? "user" : "channel", query: queryPart, anchor, items: suggestions });
  }, [editorRef, composerRef, closeMentionDropdown, normalizedChannelMentions, normalizedUserMentions, mentionState.isOpen, mentionState.type, mentionState.query]);

  useEffect(() => { closeMentionDropdown(); }, [closeMentionDropdown, channelOptions]);
  useEffect(() => { refreshMentionState(); }, [refreshMentionState, normalizedChannelMentions, normalizedUserMentions]);

  const commitMention = useCallback((item) => {
    const editor = editorRef.current;
    const range = mentionRangeRef.current;
    if (!editor || !range || !item) { closeMentionDropdown(); return; }
    const selection = typeof window !== "undefined" ? window.getSelection() : null;
    if (selection) { selection.removeAllRanges(); selection.addRange(range); }
    const token = mentionState.type === "channel" ? `#${item.value || item.label}` : `@${item.value || item.label}`;
    editor.exec?.('text', `${token} `);
    editor.focus?.();
    closeMentionDropdown();
    setTimeout(() => { try { refreshMentionState(); } catch (_) {} }, 0);
  }, [editorRef, closeMentionDropdown, refreshMentionState, mentionState.type]);

  const handleMentionSelect = useCallback((index) => {
    const item = mentionState.items[index] || mentionState.items[0];
    if (!item) { closeMentionDropdown(); return; }
    commitMention(item);
  }, [commitMention, closeMentionDropdown, mentionState.items]);

  const handleInsertMention = useCallback(() => {
    const editor = editorRef.current;
    const root = editor?.getElement?.();
    if (!editor || !root) return;

    const selection = typeof window !== "undefined" ? window.getSelection() : null;
    editor.focus?.();

    let selectionInEditor = false;
    if (selection && selection.rangeCount) {
      try {
        selectionInEditor = root.contains(selection.getRangeAt(0).startContainer);
      } catch (_) {
        selectionInEditor = false;
      }
    }

    if (!selectionInEditor && selection) {
      try {
        const range = document.createRange();
        range.selectNodeContents(root);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (_) { /* no-op */ }
    }

    editor.exec?.("text", "@");
    setTimeout(() => { try { refreshMentionState(); } catch (_) {} }, 0);
  }, [editorRef, refreshMentionState]);

  return { mentionState, setMentionState, mentionActiveIndex, setMentionActiveIndex, refreshMentionState, closeMentionDropdown, commitMention, handleMentionSelect, handleInsertMention };
}

